import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  ShoppingBagIcon, 
  UserIcon, 
  Bars3Icon,
  MagnifyingGlassIcon,
  HeartIcon,
  ArrowLeftIcon,
  Cog6ToothIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import logo from '../assets/logo.png';
import { getApiUrl } from '../config/api';
import axios from 'axios';
import { HeartIcon as SolidHeartIcon } from '@heroicons/react/24/solid';
import SearchOverlay from './SearchOverlay';
import { useAuth } from '../context/AuthContext';
import ScrollToTopLink from './ScrollToTopLink';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { getCartCount } = useCart();
  const { wishlist } = useWishlist();
  const [_, setForce] = useState(0);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  // Remove showRentalLink logic and Rental Items link from navbar
  // ... remove all code related to showRentalLink and the Rental Items <Link> ...

  useEffect(() => {
    setForce(f => f + 1);
  }, [wishlist]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const response = await axios.get(getApiUrl('/api/admin/categories'));
        setCategories(response.data);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setCategoriesError('Failed to load categories');
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  // Remove showRentalLink logic and Rental Items link from navbar
  // ... remove all code related to showRentalLink and the Rental Items <Link> ...

  console.log('Navbar wishlist:', wishlist);
  const location = useLocation();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const isAdmin = user?.isAdmin;

  const handleLogout = () => {
    logout();
    setProfileMenuOpen(false);
    navigate('/login');
  };

  const offers = [
    'Free Shipping On orders above ₹1999',
    'Grab 10% off on first Order',
    'Free Shipping On orders above ₹1999',
    'Grab 10% off on all of your Favourites',
    'Free Shipping On orders above ₹1999',
    'Grab 10% off on all of your Favourites',
  ];

  // Find the rental category object
  const rentalCategory = categories.find(cat => cat.name.toLowerCase() === 'rental');
  // Get up to 3 non-rental categories
  const topCategories = categories.filter(cat => cat.name.toLowerCase() !== 'rental').slice(0, 3);
  // Compose the final list: 3 regular + rental (if exists)
  const displayCategories = rentalCategory ? [...topCategories, rentalCategory] : topCategories;

  return (
    <nav className="bg-[#56112a] shadow fixed top-0 left-0 right-0 z-50">
      <div className="w-full bg-gradient-to-r from-[#f7e4e8] via-[#fff8f6] to-[#f7e4e8] text-[#801c33] py-2 overflow-hidden flex items-center justify-center relative z-50 border-b border-[#e6bfc8]">
        <div className="w-full whitespace-nowrap overflow-hidden">
          <div className="inline-block animate-marquee">
            <span className="flex items-center gap-2 md:gap-4 text-sm md:text-base font-medium tracking-wide">
              {offers.map((offer, idx) => (
                <React.Fragment key={idx}>
                  <SolidHeartIcon className="h-4 w-4 md:h-5 md:w-5 text-[#b5325a] inline-block flex-shrink-0" />
                  <span className="whitespace-nowrap">{offer}</span>
                  {idx !== offers.length - 1 && (
                    <span className="mx-2 md:mx-4 text-[#801c33]/40 flex-shrink-0">•</span>
                  )}
                </React.Fragment>
              ))}
            </span>

          </div>
        </div>
      </div>
      <div className="mx-auto px-2 md:px-8 flex items-center justify-between gap-x-2 lg:gap-x-4 h-12 md:h-14 text-sm">
        {/* Logo */}
        <ScrollToTopLink to="/" className="flex items-center flex-shrink-0">
          <img src={logo} alt="theVastra.store Logo" className="h-6 md:h-8 w-auto mr-2" />
          {/* <span className="text-3xl font-bold text-pink-600 tracking-widest hidden sm:inline">theVastra.store</span> */}
        </ScrollToTopLink>
        {/* Desktop Menu */}
        <div className="hidden lg:flex items-center gap-x-1 xl:gap-x-3 text-sm flex-1 min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {categoriesLoading && <p className="whitespace-nowrap">Loading categories...</p>}
          {categoriesError && <p className="text-red-500 whitespace-nowrap">{categoriesError}</p>}
          {!categoriesLoading && !categoriesError && displayCategories.map(category => (
            <ScrollToTopLink 
              key={category._id}
              to={`/products?category=${category.name.toLowerCase()}`}
              className="px-2 xl:px-3 py-2 text-white hover:text-pink-300 font-medium transition-all whitespace-nowrap flex-shrink-0"
            >
              {category.name}
            </ScrollToTopLink>
          ))}
          <ScrollToTopLink
            to="/catalogue"
            className="px-2 xl:px-3 py-2 text-white hover:text-pink-300 font-medium transition-all whitespace-nowrap flex-shrink-0"
          >
            Catalogue
          </ScrollToTopLink>
          <ScrollToTopLink
            to="/reviews"
            className="px-2 xl:px-3 py-2 text-white hover:text-pink-300 font-medium transition-all whitespace-nowrap flex-shrink-0"
          >
            Reviews
          </ScrollToTopLink>
          {user && (
            <ScrollToTopLink to="/rent/submit" className="px-2 xl:px-3 py-2 text-white hover:text-pink-300 font-medium transition-all whitespace-nowrap flex-shrink-0">Rent Your Item</ScrollToTopLink>
          )}
          {isAdmin && (
            <ScrollToTopLink to="/admin" className="px-2 xl:px-3 py-2 text-white hover:text-pink-300 font-medium flex items-center transition-all whitespace-nowrap flex-shrink-0">
              <ShieldCheckIcon className="h-5 w-5 mr-1" />
              Admin Panel
            </ScrollToTopLink>
          )}
        </div>
        {/* Search */}
        <div className="hidden md:flex items-center relative flex-shrink-0 min-w-0 w-40 lg:w-56 focus-within:w-64 lg:focus-within:w-80 transition-[width] duration-300">
          {location.pathname === '/search' ? (
            <>
              <button
                onClick={() => navigate(-1)}
                className="absolute left-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 transition z-10"
                aria-label="Back"
                tabIndex={-1}
              >
                <ArrowLeftIcon className="h-5 w-5 text-gray-700" />
              </button>
              <input
                type="text"
                placeholder="Search for products, brands and more"
                className="w-full min-w-0 pl-8 pr-3 py-1.5 rounded-full border border-gray-300 bg-white/90 focus:outline-none focus:border-pink-500 cursor-pointer text-sm"
                style={{ paddingLeft: '2.5rem' }}
                autoFocus
              />
            </>
          ) : (
            <>
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
              <input
                type="text"
                placeholder="Search for products, brands and more"
                className="w-full min-w-0 pl-8 pr-3 py-1.5 rounded-full border border-gray-300 bg-white/90 focus:outline-none focus:border-pink-500 cursor-pointer text-sm"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && searchTerm.trim()) {
                    navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
                  }
                }}
              />
            </>
          )}
        </div>
        {/* Icons */}
        <div className="navbar-icons flex flex-shrink-0 items-center justify-center gap-x-6 md:gap-x-10 text-sm">
          <Link to="/wishlist" className="relative flex items-center justify-center">
            <HeartIcon className={`h-5 w-5 sm:h-6 sm:w-6 ${wishlist.length > 0 ? 'text-pink-400' : 'text-white'} hover:text-pink-300 transition-colors`} />
            {wishlist.length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-pink-600 text-white text-xs font-bold rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4 flex items-center justify-center shadow-sm">
                {wishlist.length > 99 ? '99+' : wishlist.length}
              </span>
            )}
          </Link>
          <Link to="/cart" className="relative flex items-center justify-center">
            <ShoppingBagIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white hover:text-pink-300 transition-colors" />
            {getCartCount() > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-pink-600 text-white text-xs font-bold rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4 flex items-center justify-center shadow-sm">
                {getCartCount() > 99 ? '99+' : getCartCount()}
              </span>
            )}
          </Link>
          {user ? (
            <div className="relative flex items-center justify-center">
              <button
                className="flex items-center justify-center focus:outline-none"
                onClick={() => setProfileMenuOpen((open) => !open)}
                aria-label="Profile menu"
              >
                <UserIcon className="h-6 w-6 text-white hover:text-pink-300" />
              </button>
              {profileMenuOpen && (
                <div className="absolute right-0 mt-2 w-36 bg-white rounded-md shadow-lg py-2 z-50 border border-gray-100">
                  <Link
                    to="/profile"
                    className="block px-4 py-2 text-gray-700 hover:bg-pink-50 hover:text-pink-600"
                    onClick={() => setProfileMenuOpen(false)}
                  >
                    Profile
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="block px-4 py-2 text-gray-700 hover:bg-pink-50 hover:text-pink-600"
                      onClick={() => setProfileMenuOpen(false)}
                    >
                      Admin Panel
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-pink-50 hover:text-pink-600"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button onClick={() => navigate('/login')} aria-label="Login" className="flex items-center justify-center">
              <UserIcon className="h-6 w-6 text-white hover:text-pink-300" />
            </button>
          )}
          <button className="lg:hidden" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            <Bars3Icon className="h-7 w-7 text-white" />
          </button>
        </div>
      </div>
      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-[#56112a] flex flex-col gap-y-2 px-4 pb-4 pt-2 items-center text-center">
          {!categoriesLoading && !categoriesError && displayCategories.map(category => (
            <ScrollToTopLink 
              key={category._id}
              to={`/products?category=${category.name.toLowerCase()}`}
              className="w-full py-2 rounded text-white hover:text-pink-300 font-medium transition-all"
              onClick={() => setIsMenuOpen(false)}
            >
              {category.name}
            </ScrollToTopLink>
          ))}
          <ScrollToTopLink
            to="/catalogue"
            className="w-full py-2 rounded text-white hover:text-pink-300 font-medium transition-all"
            onClick={() => setIsMenuOpen(false)}
          >
            Catalogue
          </ScrollToTopLink>
          <ScrollToTopLink
            to="/reviews"
            className="w-full py-2 rounded text-white hover:text-pink-300 font-medium transition-all"
            onClick={() => setIsMenuOpen(false)}
          >
            Reviews
          </ScrollToTopLink>
          {user && (
            <ScrollToTopLink to="/rent/submit" className="w-full py-2 rounded text-white hover:text-pink-300 font-medium transition-all"
              onClick={() => setIsMenuOpen(false)}
            >
              Rent Your Item
            </ScrollToTopLink>
          )}
          {isAdmin && (
            <ScrollToTopLink to="/admin" className="w-full py-2 rounded text-white hover:text-pink-300 flex items-center justify-center font-medium transition-all"
              onClick={() => setIsMenuOpen(false)}
            >
              <ShieldCheckIcon className="h-5 w-5 mr-1" />
              Admin Panel
            </ScrollToTopLink>
          )}
          <div className="flex md:hidden items-center w-full mt-2 relative">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
            <input
              type="text"
              placeholder="Search"
              className="w-full pl-10 pr-4 py-2 rounded-full border border-gray-300 bg-white/90 focus:outline-none focus:border-pink-500"
              onFocus={() => setIsMenuOpen(false)}
            />
          </div>
        </div>
      )}
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </nav>
  );
};

export default Navbar; 